import { FLOW_STEPS } from '@/constants/flow-steps';
import { ERROR_PAGE_TYPES } from '@/constants/flow';
import { FPTI_COMMON_BUTTONS } from 'universal/ucs';

const { PAYROLL_PARTNER_ERROR } = FLOW_STEPS.PAYROLL_PARTNER;
const { NEED_MORE_DATA_STEP, MANUAL_REVIEW_STEP } = FLOW_STEPS.COMMON;

/**
 * Enum for Common FPTI event names.
 * @enum {string}
 */
const CommonEvents = {
  /** Back button in modal nav pressed. */
  BACK_MODAL_NAV_PRESSED: 'ucs_back_modal_nav_pressed',
  /** Close button in modal nav pressed. */
  CLOSE_MODAL_NAV_PRESSED: 'ucs_close_modal_nav_pressed',
  /** Request blocked error occurred. */
  REQUEST_BLOCKED: 'ucs_failed_customer_interaction_occurred',
  /** General failed customer interaction occured */
  FCI_ERROR: 'ucs_failed_customer_interaction_occurred',
  /** Cta button is pressed on denied error screen */
  DENIED_ERROR_CTA_PRESSED: 'ucs_denied_error_cta_pressed',
  /** Retry attempted. */
  TRY_AGAIN: 'ucs_failed_customer_interaction_occurred',
  /** Retry error occurred. */
  RETRY_ERROR: 'ucs_retry_error_occurred',
  /** Unexpected error occurred. */
  UNEXPECTED_ERROR: 'ucs_unexpected_error_occurred',
  /** Unexpected error shown. */
  UNEXPECTED_ERROR_CTA_PRESSED: 'ucs_unexpected_error_cta_pressed',
  /** Payroll provider error screen shown. */
  PAYROLL_PROVIDER_ERROR_SHOWN: 'ucs_payroll_provider_error_screen_shown',
  /** OK button pressed on payroll provider error screen. */
  PAYROLL_PROVIDER_ERROR_OK_PRESSED: 'ucs_payroll_provider_error_ok_pressed',
  /** Try again later button pressed. */
  TRY_AGAIN_LATER_PRESSED: 'ucs_failed_customer_interaction_retry_later_pressed',
  /** User is redirected to stepup auth flow */
  REDIRECT_TO_STEPUP_OCCURRED: 'ucs_redirect_to_stepup_occurred',
  /** User's session has expired */
  SESSION_EXPIRED_OCCURRED: 'ucs_session_expired_occurred',
  /** Done button is pressed */
  SESSION_EXPIRED_DONE_CTA_PRESSED: 'ucs_session_expired_done_cta_pressed',
  /** Unexpected FPTI error log error occurred */
  RECORD_FPTI_ERROR_OCCURRED: 'ucs_record_fpti_error_occurred',
};

const redirectToStepUpActivityTags = (flowName) => {
  return {
    page: flowName,
    eventName: CommonEvents.REDIRECT_TO_STEPUP_OCCURRED
  }
}
const backModalClickTags = (flowName) => {
  return {
    page: flowName,
    eventName: CommonEvents.BACK_MODAL_NAV_PRESSED,
    link: FPTI_COMMON_BUTTONS.BACK
  }
}

const closeModalClickTags = (flowName) => {
  return {
    page: flowName,
    eventName: CommonEvents.CLOSE_MODAL_NAV_PRESSED,
    link: FPTI_COMMON_BUTTONS.CLOSE
  }
}

const deniedErrorClickTags = (flowName) => {
  return {
    page: `${flowName}:denied`,
    link: 'summaryBtn',
    eventName: CommonEvents.DENIED_ERROR_CTA_PRESSED
  }
}

const needMoreDataImpressionTags = (flowName) => {
  return {
    page: `${flowName}:${NEED_MORE_DATA_STEP}`,
    errorCode: ERROR_PAGE_TYPES.NEED_MORE_DATA,
    errorDesc: `${flowName} - Need More Data`,
  };
};

const manualReviewImpressionTags = (flowName) => {
  return {
    page: `${flowName}:${MANUAL_REVIEW_STEP}`,
    errorCode: ERROR_PAGE_TYPES.MANUAL_REVIEW,
    errorDesc: `${flowName} - Manual Review`,
  };
};

const retryActivityTags = (flowName) => {
  return {
    page: `${flowName}:retry`,
    eventName: CommonEvents.TRY_AGAIN,
  };
};

const retryErrorActivityTags = (flowName) => {
  return {
    page: `${flowName}:retryError`,
    eventName: CommonEvents.RETRY_ERROR,
  };
};

const unexpectedErrorTags = (flowName, eventType) => {
  const eventMap = {
    cl: CommonEvents.UNEXPECTED_ERROR_CTA_PRESSED,
    ac: CommonEvents.UNEXPECTED_ERROR,
  };

  return {
    page: `${flowName}:unexpectedError`,
    eventName: eventMap[eventType] || CommonEvents.UNEXPECTED_ERROR,
  };
};

const payrollPartnerErrorActivityTags = (flowName = 'generic') => {
  return {
    errorPageImpressionTags: {
      page: `${flowName}:${PAYROLL_PARTNER_ERROR}`,
      errorCode: ERROR_PAGE_TYPES.PAYROLL_PROVIDER_GENERIC_ERROR,
      eventName: CommonEvents.PAYROLL_PROVIDER_ERROR_SHOWN,
    },
    okBtnClickTags: {
      link: 'okBtn',
      eventName: CommonEvents.PAYROLL_PROVIDER_ERROR_OK_PRESSED,
    },
    errorPageActivityTags: {
      page: `${flowName}:${PAYROLL_PARTNER_ERROR}`,
      eventName: CommonEvents.FCI_ERROR,
    },
  };
};

const requestBlockedErrorImpressionTags = (flowName) => {
  return {
    page: `${flowName}:requestBlockedError`,
    eventName: CommonEvents.REQUEST_BLOCKED,
  };
};

const tryAgainLaterClickTags = () => {
  return {
    link: 'retryLaterBtn',
    eventName: CommonEvents.TRY_AGAIN_LATER_PRESSED,
  };
};

const sessionExpiredImpressionTags = {
  page: 'sessionExpired',
  eventName: CommonEvents.SESSION_EXPIRED_OCCURRED,
}

const sessionExpiredDoneClickTags = {
  page: 'sessionExpired',
  link: 'doneBtn',
  eventName: CommonEvents.SESSION_EXPIRED_DONE_CTA_PRESSED
}

const recordFptiErrorTags = {
  eventName: CommonEvents.RECORD_FPTI_ERROR_OCCURRED
} 

const COMMON_FPTI_TAGS = {
  backModalClickTags,
  closeModalClickTags,
  requestBlockedErrorImpressionTags,
  deniedErrorClickTags,
  needMoreDataImpressionTags,
  manualReviewImpressionTags,
  retryActivityTags,
  retryErrorActivityTags,
  unexpectedErrorTags,
  payrollPartnerErrorActivityTags,
  tryAgainLaterClickTags,
  redirectToStepUpActivityTags,
  sessionExpiredImpressionTags,
  sessionExpiredDoneClickTags,
  recordFptiErrorTags,
};


export default COMMON_FPTI_TAGS;

